<template>
  <div class="card-body pb-0 m-0 d-flex flex-column h-100">
    <div class="chart-header">
      <div>
        <div class="d-flex justify-content-between align-items-center w-100">
          <h4 class="card-title no-select me-auto">
            <!-- {{ metric == "average" ? "Avg. Spend per Customer" : "Total Spend" }} -->
            {{ title }}
          </h4>

          <button
            v-if="fullscreen"
            id="close-modal-icon"
            class="btn btn-sm"
            @click="toggleCountryModal(false, title)"
          >
            <i class="bi bi-x-lg"></i>
          </button>

          <InfoPopover
            v-if="!fullscreen"
            class="pb-2"
            theme="sea"
            :title="title"
            :content="
              title === 'Inbound - Countries'
                ? 'Based on the time period chosen , the amount of inbound transfers by each nationality is displayed.'
                : 'Based on the time period chosen , the amount of outbound transfers by each nationality is displayed.'
            "
          />
        </div>

        <div class="card-divider m-0 p-0 py-2 mt-1">
          <span>
            <small>{{ timePeriod }}</small></span
          >
        </div>
      </div>
    </div>

    <div v-if="data.length !== 0" class="d-flex gap-3 pt-3">
      <div>
        <FilterMenu
          label="Country"
          :sort-is-active="sortOption == sortOptions.nationality"
          :reverse-sorting="sortOption == 'reverse-0'"
          @click="sortData(sortOptions.nationality)"
        />
      </div>
      <div>
        <FilterMenu
          :label="
            (metric === 'average' ? 'Avg.' : 'Total') + ' value of transfers'
          "
          :sort-is-active="sortOption == sortOptions.count"
          :reverse-sorting="sortOption == 'reverse-1'"
          @click="sortData(sortOptions.count)"
        />
      </div>
    </div>

    <div
      v-if="data.length !== 0"
      class="pt-3"
      :class="[fullscreen ? 'full-chart-wrapper' : 'h30v']"
    >
      <BarChart
        :style="
          fullscreen
            ? `height: ${
                (chartData.labels ?? []).length * 35 + 350
              }px !important; `
            : ''
        "
        :class="[fullscreen ? '' : 'h30v']"
        :chart-data="chartData"
        :options="options"
      />
    </div>
    <CardsEmptyState v-else min-height="33" class="h-100" />

    <div
      v-if="!fullscreen && (chartData.labels ?? []).length >= 7"
      class="mt-2 card-link cp text-end"
    >
      <div
        class="cp d-flex justify-content-end align-items-center gap-2 py-2"
        @click="
          toggleCountryModal(true, title);
          trackViewAll(title);
        "
      >
        <small class="view-more-btn py-2 cp">View All Countries</small>
        <img
          class="arrow-right"
          src="../assets/images/svg/arrow-point-right.svg"
          alt="arrow"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, inject, ref } from "vue";
import { BarChart } from "vue-chart-3";
import { Chart } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

import FilterMenu from "./FilterMenu.vue";
import "chartjs-adapter-moment";
import "chartjs-plugin-style";
import InfoPopover from "./InfoPopover.vue";
import CardsEmptyState from "./CardsEmptyState.vue";
import { useStore } from "vuex";

export default {
  components: { BarChart, FilterMenu, InfoPopover, CardsEmptyState },
  props: {
    title: {
      type: String,
      default: "Countries",
    },
    metric: {
      default: "average",
    },
    timePeriod: {
      type: String,
      default: "Last 30 days",
    },
    data: {
      type: [Object, Array],
      default: () => [],
    },
    summary: {
      type: Object,
    },
    period: {
      default: 30,
    },
    loading: {
      default: false,
    },
    fullscreen: {
      default: false,
    },
  },
  emits: ["toggleCountryModal"],
  setup(props, { emit }) {
    const capitalizeFirstLetterForEachWord = inject(
      "capitalizeFirstLetterForEachWord"
    );

    Chart.register(ChartDataLabels);
    Chart.defaults.set("plugins.datalabels", {
      color: "#FE777B00",
    });

    const sortOption = ref(1);

    const sortOptions = {
      nationality: 0,
      count: 1,
    };

    const numberWithCommas = (x) => {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    const compactNumber = inject("compactNumber");

    const toggleCountryModal = (value, title) => {
      //console.log('title: ', title)
      emit("toggleCountryModal", value, title);
    };

    const sortData = (option) => {
      sortOption.value =
        sortOption.value == option ? `reverse-${option}` : option;
    };

    const store = useStore();
    const currency = computed(() => {
      return store.state.currency;
    });

    const rawChartData = computed(() => {
      let keys = [];
      let values = [];

      //console.log(props.data);

      let entries = [];

      if (props.data.length) {
        let _data = [...props.data];

        if (
          sortOption.value == "reverse-0" ||
          sortOption.value == "reverse-1"
        ) {
          entries = _data.sort((a, b) => {
            if (sortOption.value == "reverse-0") {
              // sort alphabetically from A to Z
              return (a.nationality ?? "NA").toLowerCase() <
                (b.nationality ?? "NA").toLowerCase()
                ? 1
                : -1;
            } else {
              // sort from highest to smallest
              return Math.abs(b.amount) < Math.abs(a.amount) ? 1 : -1;
            }
          });
        } else {
          entries = _data.sort((a, b) => {
            if (sortOption.value == 0) {
              // sort alphabetically from A to Z
              return (a.nationality ?? "NA").toLowerCase() <
                (b.nationality ?? "NA").toLowerCase()
                ? -1
                : 1;
            } else {
              // sort from highest to smallest
              return Math.abs(b.amount) < Math.abs(a.amount) ? -1 : 1;
            }
          });
        }
      }

      entries.map((_datum) => {
        if (props.fullscreen || keys.length < 7) {
          keys.push(_datum.nationality ?? "NA");
          values.push(_datum.amount);
        }
      });

      return {
        keys: keys,
        values: values.map((val) => Math.abs(Math.round(val))),
      };
    });

    const chartData = computed(() => ({
      labels: rawChartData.value.keys,
      datasets: [
        {
          indexAxis: "y",
          // label: "Current",
          data: rawChartData.value.values,
          backgroundColor: rawChartData.value.values.map((_, index, arr) => {
            let len = arr.length;
            let percentage = parseInt(((len + 1 - index) / (len + 1)) * 100);
            // let percentage = 100 - (index * 10);

            if (percentage == 100) {
              return "#469a93";
            }

            return percentage > 9
              ? `#469a93${percentage}`
              : `#469a930${percentage}`;
          }),
          borderWidth: 1,
          borderRadius: 5,
          // barThickness: 34.5,
          // tension: 0.05,
          datalabels: {
            align: "end",
            anchor: "end",
            formatter: (value) => {
              return `${currency.value} ${compactNumber(value)}`;
            },
            color: "#000",
          },
        },
      ],
    }));

    const options = ref({
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          position: "top",
          align: "start",
          reverse: true,
          labels: {
            usePointStyle: true,
            boxWidth: 10,
            boxHeight: 10,
          },
        },
        tooltip: {
          enabled: true,
          // position: "nearest",
          usePointStyle: true,
          backgroundColor: "#f8f9fb",
          titleColor: "black",
          bodyColor: "black",
          // padding: 15,
          bodyFont: {
            size: 12,
            lineHeight: 1.9,
          },
          displayColors: true,
          callbacks: {
            title: () => {
              return;
            },
            label: (context) => {
              return `  ${capitalizeFirstLetterForEachWord(context.label)}    ${
                currency.value
              }${context.formattedValue}`;
            },
            labelPointStyle: function () {
              return {
                pointStyle: "circle",
                rotation: 0,
              };
            },
          },
        },
        ChartDataLabels,
      },
      scales: {
        y: {
          ticks: {
            color: "#200e32",
            callback: function (value) {
              return capitalizeFirstLetterForEachWord(
                this.getLabelForValue(value)
              );
            },
          },
          min: 0,
          grid: {
            display: false,
            drawBorder: false,
          },
        },
        x: {
          //TODO add margin on x-axis
          //min: Heighest value * 1.1,
          //max: Heighest value * 1.1,
          ticks: {
            display: false,
          },
          grid: {
            display: false,
            drawBorder: false,
          },
        },
      },

      indexAxis: "y",
    });

    return {
      chartData,
      options,
      numberWithCommas,
      sortData,
      sortOption,
      sortOptions,
      toggleCountryModal,
    };
  },
  methods: {
    trackViewAll() {
      this.$gtag.event(
        `MAY23_click_viewall_countries_${this.title?.toLowerCase()}`,
        {
          event_category: "Engagement",
          event_label: `MAY23_click_viewall_countries_${this.title?.toLowerCase()}`,
          value: 1,
        }
      );
    },
  },
};
</script>

<style scoped>
.chart-header {
  border-bottom: 1px solid #dee2e6;
}
.active-text {
  color: #469b93 !important;
}

.alt-text {
  color: #97aabd !important;
}
.h330 {
  height: 330px;
}
.h30v {
  height: 30vh;
}
.h100v {
  height: 100vh;
}

.overflow-scroll {
  overflow-y: auto !important;
}
.percentage {
  background-color: var(--sea-10);
  color: var(--sea);
  font-size: 0.8rem !important;
  padding: 3px 7px;
  border-radius: 7px;
}
.negative {
  background-color: rgba(223, 41, 53, 0.1);
  color: #df2935ec;
}

.full-chart-wrapper {
  overflow-y: scroll !important;
  /* background: red; */
}
</style>
